import { useState, useEffect } from 'react';

/**
 * Detect the current browser by inspecting the user agent string.
 * Returns one of: 'chrome' | 'edge' | 'opera' | 'firefox' | 'safari' | 'other'
 *
 * Order matters — Opera and Edge both contain 'Chrome' in their UA, so they
 * must be checked before the generic Chrome check.
 */
export function detectBrowser() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  if (/OPR\/|Opera\//i.test(ua)) return 'opera';
  if (/Edg\//i.test(ua)) return 'edge';
  if (/Chrome\//i.test(ua)) return 'chrome';
  if (/Firefox\//i.test(ua)) return 'firefox';
  if (/Safari\//i.test(ua)) return 'safari';
  return 'other';
}

/**
 * Returns true for browsers that natively show an install icon in the address
 * bar when a PWA is installable (Chrome, Edge).
 *
 * Opera fires `beforeinstallprompt` but does NOT show an address-bar icon —
 * the install entry point is buried in Opera's own menu.
 * Firefox does NOT fire `beforeinstallprompt` at all; it has its own separate
 * install UI.
 * Safari does not support `beforeinstallprompt` at all.
 */
export function hasBrowserNativeInstallIcon() {
  const browser = detectBrowser();
  return browser === 'chrome' || browser === 'edge';
}

/**
 * Hook to manage PWA install prompt
 * Captures the beforeinstallprompt event and provides methods to show install prompt
 */
export function usePwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) {
      return { outcome: 'unavailable' };
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    // Clear the deferred prompt - it can only be used once
    setDeferredPrompt(null);
    setCanInstall(false);

    return { outcome };
  };

  return { 
    canInstall, 
    isInstalled, 
    promptInstall 
  };
}

/**
 * Check if the app is running as an installed PWA
 */
export function isPwaInstalled() {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
}

/**
 * Check if the device is iOS (for showing manual install instructions)
 */
export function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export default usePwaInstallPrompt;
